// CSS 단위가 포함된 문자열을 계산할 수 있는 함수
// e.g. const height = 4.25; calcCSS`4${height}rem` = '17rem'
// e.g. const height = 3; calcCSS`4 * ${height}rem` = '12rem'
// e.g. const height = 5; calcCSS`4 + ${height}rem` = '9rem'
// e.g. const dynamic = flag ? 2 : 3; const height = 5; calcCSS`${dynamic} + ${height}rem` = '10rem' or '15rem'

class CalcCSSError extends Error {
  constructor(message: string) {
    super(`CalcCSSError: ${message}`);
  }
}

export function calcCSS(
  strings: TemplateStringsArray,
  ...values: (string | number)[]
): string {
  if (values.length > 2) throw new CalcCSSError('Too many values');

  if (values[1] === undefined) {
    if (typeof values[0] === 'string') {
      const unitMatches = values[0].match(/([0-9]+)([a-z]+)/);
      if (unitMatches) {
        values[0] = Number(unitMatches[1]);
        // @ts-expect-error 강제 주입
        strings[1] = unitMatches[2];
      } else if (!isNaN(Number(values[0]))) {
        return `${values[0]}px`;
      } else {
        throw new CalcCSSError(`Invalid template value: ${values[0]}`);
      }
    }

    // e.g. calcCSS`${height}` = '4px'
    if (strings[0] === '') {
      return `${values[0]}px`;
    }

    const factor = Number(strings[0]);

    if (strings[1] === undefined) {
      if (factor) {
        // e.g. calcCSS`4` = '4px'
        return `${factor * values[0]}px`;
      } else {
        // e.g. calcCSS`${height}rem` = '4rem'
        return `${values[0]}${strings[0]}`;
      }
    }

    if (factor) {
      // e.g. calcCSS`4${height}rem` = '17rem'
      return `${factor * values[0]}${strings[1]}`;
    }

    const staticAndVariable = strings[0].match(/([0-9]+).(\+|-|\*|\/)./);
    if (staticAndVariable) {
      // e.g. calcCSS`4 + ${height}rem` = '9rem'
      switch (staticAndVariable[2]) {
        case '+':
          return `${Number(staticAndVariable[1]) + values[0]}${strings[1]}`;
        case '-':
          return `${Number(staticAndVariable[1]) - values[0]}${strings[1]}`;
        case '*':
          return `${Number(staticAndVariable[1]) * values[0]}${strings[1]}`;
        case '/':
          return `${Number(staticAndVariable[1]) / values[0]}${strings[1]}`;
      }
    }
  } else {
    if (typeof values[0] === 'string') {
      // split the string into number and unit
      const unitMatches = values[0].match(/([0-9]+)([a-z]+)/);
      if (unitMatches) {
        [values[0], values[1]] = [values[1], values[0]];
      }

      if (!isNaN(Number(values[0]))) {
        values[0] = Number(values[0]);
      } else {
        throw new Error(`Invalid template value: ${values[0]}`);
      }
    }

    if (typeof values[1] === 'string') {
      // split the string into number and unit
      const unitMatches = values[1].match(/([0-9]+)([a-z]+)/);
      if (unitMatches) {
        values[1] = Number(unitMatches[1]);
        // @ts-expect-error 강제 주입
        strings[2] = unitMatches[2];
      } else if (!isNaN(Number(values[1]))) {
        return `${values[0]}px`;
      } else {
        throw new Error(
          `Invalid template value: ${values[1]} in ${strings} ${values}`
        );
      }
    }

    const variableAndVariable = strings[1].match(/.?(\+|-|\*|\/).?/);
    if (variableAndVariable) {
      // e.g. calcCSS`${dynamic} * ${height}rem` = '10rem' or '15rem'
      switch (variableAndVariable[1]) {
        case '+':
          return `${values[0] + values[1]}${strings[2]}`;
        case '-':
          return `${values[0] - values[1]}${strings[2]}`;
        case '*':
          return `${values[0] * values[1]}${strings[2]}`;
        case '/':
          return `${values[0] / values[1]}${strings[2]}`;
      }
    }
  }

  throw new Error(`Invalid CSS calculation: ${strings} ${values}`);
}
